import { parsePhoneNumberFromString } from 'libphonenumber-js'
import { trimEnd } from 'lodash'
import PropTypes from 'prop-types'
import React from 'react'
import { conformToMask } from 'react-text-mask'

import TextField from 'components/_form/TextField'

const RUSSIAN_PHONE_PREFIX = '+7('

const PhoneField = ({ phoneMask, alpha2Code, onBlur, onFocus, ...props }) => {
  const isRussianPhone = alpha2Code === 'RU'
  const symbols = React.useMemo(
    () =>
      phoneMask
        .reduce((memo, letter) => {
          if (!(letter instanceof RegExp)) {
            memo.push(letter)
          }
          return memo
        }, [])
        .join(''),
    [phoneMask],
  )

  /*
   * Normalize phone number to remove code from value
   */
  const normalizePhoneNumber = React.useCallback(
    value => {
      if (value) {
        const phoneNumber = parsePhoneNumberFromString(value, alpha2Code)
        const nationalNumber = phoneNumber?.nationalNumber || value
        const result = conformToMask(nationalNumber, phoneMask, {
          guide: false,
        }).conformedValue

        if (result && result.length > 0) {
          if (isRussianPhone) {
            return result
          }

          return trimEnd(result, symbols)
        }

        return result
      }

      return value
    },
    [phoneMask, symbols, alpha2Code, isRussianPhone],
  )

  const handleFocus = React.useCallback(
    (event, inputOptions) => {
      if (isRussianPhone && !inputOptions.value) {
        inputOptions.onChange(RUSSIAN_PHONE_PREFIX)
      }

      onFocus?.(event, inputOptions)
    },
    [isRussianPhone, onFocus],
  )

  const handleBlur = React.useCallback(
    (event, inputOptions) => {
      if (isRussianPhone && inputOptions.value === RUSSIAN_PHONE_PREFIX) {
        if (event?.target) {
          event.target.value = ''
        }

        inputOptions.onChange('')
      }

      onBlur?.(event, inputOptions)
    },
    [isRussianPhone, onBlur],
  )

  return (
    <TextField
      {...props}
      format={normalizePhoneNumber}
      onBlur={handleBlur}
      onFocus={handleFocus}
      parse={normalizePhoneNumber}
    />
  )
}

PhoneField.propTypes = {
  phoneMask: PropTypes.array.isRequired,
  alpha2Code: PropTypes.string.isRequired,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
}

export default PhoneField
